export const PWA_UPDATE_EVENT_NAME = 'omni:pwa-update'

/**
 * Return a same-origin route with its query and hash, removing internal PWA navigation markers.
 * The local url object validates the origin and removes __pwa_reload / __pwa_recover before the
 * result is stored as an intended route or used for the final reload.
 *
 * @param {string} value Absolute URL or portal-relative destination.
 * @param {string} origin Origin of the portal that is allowed to receive the navigation.
 * @returns {string} Cleaned pathname, query and hash.
 */
export const cleanUpdateDestination = (value, origin) => {
  const url = new URL(value, origin)
  if (url.origin !== origin) throw new Error('Update destination must belong to this portal')
  url.searchParams.delete('__pwa_reload')
  url.searchParams.delete('__pwa_recover')
  return url.pathname + url.search + url.hash
}

/**
 * Validate the release catalogue and determine whether this client can keep using its assets.
 * release.current identifies the deployed build; release.supported lists all retained builds,
 * including the current one. A supported older build remains usable without immediate updating.
 * The catalogue must have schema 1 and match this portal; invalid metadata throws rather than
 * classifying the client as expired.
 *
 * @param {object} release Metadata from pwa-release.json.
 * @param {string} buildId Identity embedded in the currently loaded client.
 * @param {string} portal Expected portal identifier: admin or customer.
 * @returns {'current'|'supported'|'unsupported'} Support status of the loaded client.
 */
export const classifyRelease = (release, buildId, portal) => {
  if (
    release?.schema !== 1 ||
    release.portal !== portal ||
    typeof release.current !== 'string' ||
    !/^[a-zA-Z0-9._-]{1,180}$/.test(release.current) ||
    !Array.isArray(release.supported) ||
    !release.supported.includes(release.current) ||
    !release.supported.every((id) => typeof id === 'string' && /^[a-zA-Z0-9._-]{1,180}$/.test(id))
  )
    throw new Error('Invalid portal release metadata')
  return release.current === buildId ? 'current' : release.supported.includes(buildId) ? 'supported' : 'unsupported'
}

/**
 * Read an active or waiting service worker's build identity without activating it.
 * channel is a dedicated MessageChannel for the reply. timeout bounds the wait, while finish
 * closes the reply port and clears the timer. Missing, unreachable or older workers resolve null.
 *
 * @param {ServiceWorker|null} worker Worker to query with OMNI_GET_BUILD.
 * @param {number} [timeoutMs] Maximum reply wait in milliseconds.
 * @returns {Promise<string|null>} Reported build identity, or null when it cannot be read.
 */
export const getWorkerBuild = (worker, timeoutMs = 2000) => {
  if (!worker) return Promise.resolve(null)
  return new Promise((resolve) => {
    const channel = new MessageChannel()
    const finish = (buildId) => {
      clearTimeout(timeout)
      channel.port1.close()
      resolve(buildId)
    }
    const timeout = setTimeout(() => finish(null), timeoutMs)
    channel.port1.onmessage = (event) => finish(event.data?.buildId || null)
    try {
      worker.postMessage({ type: 'OMNI_GET_BUILD' }, [channel.port2])
    } catch {
      finish(null)
    }
  })
}

/**
 * Create the update controller used by boot, router guards and PwaUpdateNotice in one app window.
 * The shared state drives the UI; checks only detect updates, while update() requires user confirmation.
 * pwa-release.json contains the retained release catalogue; pwa-build.json identifies the served image.
 *
 * Private variables:
 * - loadRelease / loadBuild: supplied fetch functions or the default uncached metadata requests.
 * - checkedAt: timestamp of the last successful catalogue check, used for thirty-second result reuse.
 * - checking: shared promise for an in-flight check, preventing overlapping metadata requests.
 * - registration: worker registration remembered from an availability event for use during updating.
 * - reloading: navigation has been requested; blocks repeat updates and keeps the button busy until unload.
 *
 * @param {object} options Controller configuration.
 * @param {object} options.state Pinia update state shared with the notification component.
 * @param {string} options.buildId Identity of the loaded client build.
 * @param {string} options.portal Portal identifier used to validate all metadata.
 * @param {boolean} options.enabled Whether release checks and availability detection are enabled.
 * @param {Window} [options.browser] Window providing navigation, fetch and service worker access.
 * @param {Function} [options.fetchRelease] Optional replacement for loading pwa-release.json.
 * @param {Function} [options.fetchBuild] Optional replacement for loading pwa-build.json.
 * @param {Function} [options.workerBuild] Function that reads a worker's build identity.
 * @param {number} [options.timeoutMs] Maximum wait for worker activation in milliseconds.
 * @returns {object} check, available, beforeNavigation, failedNavigation and update methods.
 */
export const createPwaUpdateController = ({
  state,
  buildId,
  portal,
  enabled,
  browser = window,
  fetchRelease,
  fetchBuild,
  workerBuild = getWorkerBuild,
  timeoutMs = 20000,
}) => {
  const loadRelease =
    fetchRelease ||
    (async () => {
      const response = await browser.fetch('/pwa-release.json', { cache: 'no-store', signal: AbortSignal.timeout(5000) })
      if (!response.ok) throw new Error('Release check failed')
      return response.json()
    })
  const loadBuild =
    fetchBuild ||
    (async () => {
      const response = await browser.fetch('/pwa-build.json', { cache: 'no-store', signal: AbortSignal.timeout(5000) })
      return response.ok ? response.json() : null
    })
  let checkedAt = 0
  let checking = null
  let registration = null
  let reloading = false

  /**
   * Check the catalogue and update the UI state, sharing requests and reusing successful results.
   * release is the server catalogue; status classifies this client's build. When unsupported,
   * served checks the image's own manifest: deployment may already serve a new image before its
   * catalogue is committed, so that image must not be mistaken for an expired client.
   * A different currentBuild resets dismissed so a newer release can show the full notice again.
   * Failures set status to unknown and checkFailed, while preserving any confirmed unsupported flag.
   *
   * @param {boolean} [force] Bypass result reuse, but still share an in-flight request.
   * @returns {Promise<string>} current, supported, unsupported or unknown.
   */
  const check = async (force = false) => {
    if (!enabled) return 'current'
    if (checking) return checking
    if (!force && Date.now() - checkedAt < 30000) return state.status
    checking = (async () => {
      try {
        const release = await loadRelease()
        const status = classifyRelease(release, buildId, portal)
        if (status === 'unsupported') {
          let served
          try {
            served = await loadBuild()
          } catch {
            served = null
          }
          if (served?.schema === 1 && served.portal === portal && served.buildId === buildId) {
            state.status = 'unknown'
            state.unsupported = false
            state.available = false
            state.checkFailed = true
            checkedAt = 0
            return state.status
          }
        }
        if (release.current !== state.currentBuild) state.dismissed = false
        state.status = status
        state.currentBuild = release.current
        state.unsupported = state.status === 'unsupported'
        state.available = state.status !== 'current'
        state.error = false
        state.checkFailed = false
        checkedAt = Date.now()
      } catch {
        state.status = 'unknown'
        state.checkFailed = true
        checkedAt = 0
      }
      return state.status
    })()
    try {
      return await checking
    } finally {
      checking = null
    }
  }

  /**
   * Record a waiting worker registration and make the update action visible.
   * registration retains this event's worker registration as a fallback for update(); availability
   * alone neither sends SKIP_WAITING nor reloads the page.
   *
   * @param {ServiceWorkerRegistration} waitingRegistration Registration supplied by the worker event.
   */
  const available = (waitingRegistration) => {
    if (!enabled) return
    registration = waitingRegistration
    state.available = true
  }

  /**
   * Check support before opening a route and keep the destination for update recovery.
   * state.pendingDestination records the attempted route for possible lazy-load errors.
   * state.destination becomes the reload target when a confirmed unsupported build blocks navigation.
   * initial allows the HTML recovery UI to show that block before Vue has mounted.
   *
   * @param {string} destination Intended route, including query and hash.
   * @param {boolean} [initial] Whether this is the application's initial navigation.
   * @returns {Promise<boolean>} Whether the router may continue.
   */
  const beforeNavigation = async (destination, initial = false) => {
    state.pendingDestination = cleanUpdateDestination(destination, browser.location.origin)
    await check()
    if (!state.unsupported) return true
    state.destination = cleanUpdateDestination(destination, browser.location.origin)
    state.dismissed = false
    if (initial) browser.__OMNI_PWA_RECOVERY__?.show('unsupported', state.destination)
    return false
  }

  /**
   * Offer recovery when missing app code prevents a route from opening.
   * state.destination prefers the failed route, then pendingDestination, then the current URL.
   * loadFailed exposes the recovery state immediately; a forced catalogue check determines support
   * separately because a missing chunk alone does not prove that the client is unsupported.
   *
   * @param {string} [destination] Failed route, if known.
   */
  const failedNavigation = (destination) => {
    state.destination = cleanUpdateDestination(destination || state.pendingDestination || browser.location.href, browser.location.origin)
    state.loadFailed = true
    state.available = true
    state.dismissed = false
    void check(true)
  }

  /**
   * After user confirmation, verify the target release, activate its worker and reload this window.
   * targetBuild is the verified server release. container provides service worker access;
   * activeRegistration is the current registration or the one remembered by available().
   * started bounds activation time; requestedWorker avoids repeatedly messaging the same waiting worker.
   * Only a worker reporting targetBuild receives SKIP_WAITING, and navigation waits until it controls
   * this page. destination preserves the route, query and hash; url adds the temporary reload marker.
   * state.updating and reloading keep the button disabled through navigation. A failure sets error,
   * reopens the notice and permits retry without automatically reloading or clearing browser caches.
   *
   * @returns {Promise<boolean>} Whether navigation to the updated app was requested.
   */
  const update = async () => {
    if (state.updating || reloading) return false
    state.updating = true
    state.error = false
    try {
      await check(true)
      if (state.status === 'unknown') throw new Error('Release could not be verified')
      const targetBuild = state.currentBuild
      const container = browser.navigator.serviceWorker
      const activeRegistration = container ? (await container.getRegistration()) || registration : null
      if (activeRegistration) {
        await activeRegistration.update()
        const started = Date.now()
        let requestedWorker = null
        while (Date.now() - started < timeoutMs) {
          if ((await workerBuild(container.controller)) === targetBuild) break
          const waiting = activeRegistration.waiting
          if (waiting && waiting !== requestedWorker && (await workerBuild(waiting)) === targetBuild) {
            requestedWorker = waiting
            waiting.postMessage({ type: 'SKIP_WAITING' })
          }
          await new Promise((resolve) => browser.setTimeout(resolve, 250))
        }
        if ((await workerBuild(container.controller)) !== targetBuild) throw new Error('Worker activation timed out')
      }
      const destination = cleanUpdateDestination(state.destination || browser.location.href, browser.location.origin)
      const url = new URL(destination, browser.location.origin)
      url.searchParams.set('__pwa_reload', targetBuild)
      reloading = true
      browser.location.replace(url.href)
      return true
    } catch {
      reloading = false
      state.error = true
      state.dismissed = false
      return false
    } finally {
      if (!reloading) state.updating = false
    }
  }

  return { check, available, beforeNavigation, failedNavigation, update }
}

let controller
/**
 * Install the controller for this loaded app window and return it to the boot file.
 * The module-level controller is also used by the router and update component entry points below.
 *
 * @param {object} options Configuration accepted by createPwaUpdateController.
 * @returns {object} Installed update controller.
 */
export const installPwaUpdates = (options) => {
  controller = createPwaUpdateController(options)
  return controller
}
/**
 * Forward a router guard to the installed controller, or allow navigation before installation.
 *
 * @param {string} destination Intended route, including query and hash.
 * @param {boolean} [initial] Whether the application is performing its initial navigation.
 * @returns {Promise<boolean>} Whether the router may continue.
 */
export const checkPwaNavigation = async (destination, initial) => {
  return controller ? controller.beforeNavigation(destination, initial) : true
}
/**
 * Forward a missing-chunk or import failure to the installed controller's recovery state.
 *
 * @param {string} [destination] Route that failed to load, if known.
 */
export const reportPwaLoadFailure = (destination) => {
  controller?.failedNavigation(destination)
}
/**
 * Run the installed controller's user-confirmed update action from the notification component.
 *
 * @returns {Promise<boolean>|undefined} Update result, or undefined before controller installation.
 */
export const updatePwaApp = () => {
  return controller?.update()
}
